import { Link, useSearchParams } from 'react-router-dom';
import { BlogCard } from '../components/BlogCard';
import { CTASection } from '../components/CTASection';
import { PageHero } from '../components/PageHero';
import { Section, SectionHeading, Reveal } from '../components/Section';
import { blogPosts, categories, featuredPosts } from '../lib/blog';
import { Icon } from '../lib/icons';
import { SEO } from '../lib/seo';
import { cn } from '../lib/cn';

export function Blog() {
  const [searchParams] = useSearchParams();
  const activeCategory = searchParams.get('category') ?? 'All';
  const posts =
    activeCategory === 'All' ? blogPosts : blogPosts.filter((post) => post.category === activeCategory);

  return (
    <>
      <SEO
        title="Blog"
        description="Practical MaytoMation articles about business automation, internal tools, managed hosting, networking, infrastructure, and AI systems."
      />
      <PageHero
        eyebrow="Blog"
        title="Practical notes on software, automation, infrastructure, and support."
        body="Readable guidance for business owners, contractors, property owners, operations managers, and teams trying to make technology more reliable."
        primary={{ label: 'Book Consultation', href: '/booking' }}
        secondary={{ label: 'View Services', href: '/services' }}
        highlights={['Automation', 'Networking', 'Managed hosting', 'AI systems']}
      />

      <Section>
        <SectionHeading
          eyebrow="Featured"
          title="Recommended articles."
          body="Start with these if you are thinking about automation, internal tools, or practical AI."
        />
        <div className="mt-12 grid gap-5 lg:grid-cols-3">
          {featuredPosts.map((post) => (
            <Reveal key={post.slug}>
              <BlogCard post={post} />
            </Reveal>
          ))}
        </div>
      </Section>

      <Section className="bg-white">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeading
            eyebrow="Articles"
            title="Browse by category."
            body="The blog is powered by Markdown posts with SEO-friendly URLs, tags, featured articles, and related article recommendations."
          />
          <div className="flex flex-wrap gap-2">
            {['All', ...categories].map((category) => (
              <Link
                key={category}
                to={category === 'All' ? '/blog' : `/blog?category=${encodeURIComponent(category)}`}
                className={cn(
                  'rounded-full border px-4 py-2 text-sm font-bold transition',
                  activeCategory === category
                    ? 'border-brand-navy bg-brand-navy text-white'
                    : 'border-brand-border bg-white text-brand-navy hover:bg-brand-background'
                )}
              >
                {category}
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-12 grid gap-5 lg:grid-cols-3">
          {posts.map((post) => (
            <Reveal key={post.slug}>
              <article className="h-full">
                <BlogCard post={post} />
                <div className="mt-3 flex flex-wrap gap-2 px-1">
                  {post.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 rounded-full bg-brand-background px-3 py-1 text-xs font-bold text-brand-muted"
                    >
                      <Icon name="check" className="h-3 w-3 text-brand-teal" />
                      {tag}
                    </span>
                  ))}
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </Section>

      <CTASection title="Reading because something in your operation feels harder than it should?" button="Talk Through It" />
    </>
  );
}
